/**
 * Network rules — `/countries/:cc/admin/network-rules`. Phase 2.5b Part B, the
 * D7 premises-control UI.
 *
 * AUTHORED (no Figma exists for the admin surface); assembled from existing
 * primitives only.
 *
 * ===========================================================================
 * THE ARMED / UNARMED STATE IS THE FIRST THING ON THE SCREEN
 * ===========================================================================
 * D7's default is armed-but-empty: a country with ZERO active rules is
 * UNRESTRICTED for its users. That state must be said in words, calmly —
 * an empty table alone reads as "not set up yet", when it actually means
 * "everyone can work from anywhere". Equally, the armed state must say what
 * it now costs: users outside every listed network (in every armed country
 * they hold) are refused, unless they hold remote access.
 *
 * ===========================================================================
 * "YOUR CURRENT IP" COMES FROM THE API, NOT THE BROWSER
 * ===========================================================================
 * The list envelope's `client_ip` is resolved by the SAME helper the premises
 * guard uses (same TRUST_PROXY semantics — `network-rules.controller.ts`
 * header). That is the whole point: the address the admin registers from this
 * helper is, by construction, the address the guard will test. A browser-side
 * guess (WebRTC tricks, a third-party echo) could differ from the guard's view
 * and arm an office with a network that never matches. The helper prefills a
 * SINGLE-HOST rule (/32, /128 for IPv6) — the admin widens it to the office
 * network deliberately, never accidentally.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, EmptyState, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES } from '../../domain/countries';
import { relativeTime } from '../../domain/events';
import { ApiRequestError } from '../../lib/api';
import {
  NETWORK_RULE_CIDR_MAX,
  NETWORK_RULE_LABEL_MAX,
  adminInfraKeys,
  createNetworkRule,
  deactivateNetworkRule,
  fetchNetworkRules,
} from './infra-api';
import type { NetworkRuleDto } from './infra-api';
import { ConfirmDialog } from './ConfirmDialog';
import { NoAdminAccess, errorSentence } from './states';

/** `196.223.14.5` → `196.223.14.5/32`; `2c0f:…:1` → `…/128`. Single host, both families. */
export function singleHostCidr(ip: string): string {
  return ip.includes(':') ? `${ip}/128` : `${ip}/32`;
}

/**
 * The `reason` the API's 422 carries in `details` (D7.6 — ipaddr.js's verdict).
 * Preferred over the envelope message because it names the fix ("prefix length
 * must be 0-32"), not just the fact.
 */
export function cidrErrorFrom(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (error.status !== 422 && error.status !== 400) return null;
  const details = error.details as { field?: unknown; reason?: unknown } | undefined;
  if (details !== undefined && typeof details.reason === 'string' && details.reason !== '') {
    return details.reason;
  }
  return error.message;
}

export function NetworkRulesPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [label, setLabel] = useState('');
  const [cidr, setCidr] = useState('');
  const [cidrError, setCidrError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<NetworkRuleDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const cidrFieldRef = useRef<HTMLInputElement | null>(null);

  const rules = useQuery({
    queryKey: adminInfraKeys.networkRules(countryCode),
    queryFn: ({ signal }) => fetchNetworkRules(countryCode, signal),
    enabled: permitted,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: adminInfraKeys.networkRules(countryCode) });
  };

  const create = useMutation({
    mutationFn: (body: { cidr: string; label: string }) => createNetworkRule(countryCode, body),
    onSuccess: () => {
      setLabel('');
      setCidr('');
      setCidrError(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      const reason = cidrErrorFrom(error);
      if (reason !== null) {
        // A bad CIDR belongs UNDER THE FIELD, where the fix happens — not in a
        // page-level banner the operator has to map back to an input.
        setCidrError(reason);
      } else {
        setActionError(errorSentence(error, 'The network rule could not be added.'));
      }
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateNetworkRule(countryCode, id),
    onSuccess: () => {
      setDeactivating(null);
      setActionError(null);
      invalidate();
    },
    onError: (error) => {
      setDeactivating(null);
      setActionError(errorSentence(error, 'The network rule could not be deactivated.'));
      // 409 = somebody else already deactivated it; make the list say so.
      if (error instanceof ApiRequestError && error.status === 409) invalidate();
    },
  });

  if (!permitted) return <NoAdminAccess />;

  const rows = rules.data?.data ?? [];
  const activeRules = rows.filter((row) => row.active);
  const clientIp = rules.data?.client_ip ?? null;
  const countryName = COUNTRIES[countryCode].name;

  const submit = (): void => {
    const trimmedLabel = label.trim();
    const trimmedCidr = cidr.trim();
    if (trimmedLabel === '' || trimmedCidr === '' || create.isPending) return;
    create.mutate({ cidr: trimmedCidr, label: trimmedLabel });
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Network rules"
        subtitle={`The office networks ${countryName} users may use the app from. Signing in always works — the rules apply from the first screen after it.`}
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        {/* The armed/unarmed truth, stated in one calm sentence. */}
        {rules.isSuccess ? (
          activeRules.length === 0 ? (
            <Alert tone="info" title="No rules — unrestricted">
              <p>{`${countryName} users can work from any network until a rule is added here.`}</p>
            </Alert>
          ) : (
            <Alert tone="info" title={`Armed — ${String(activeRules.length)} ${activeRules.length === 1 ? 'network admits' : 'networks admit'} ${countryName} users`}>
              <p>
                Users outside every listed network are refused after sign-in, unless they hold
                remote access or a country of theirs has no rules.
              </p>
            </Alert>
          )
        ) : null}

        <Card title="Add a network">
          {clientIp === null ? null : (
            <p className="text-body13 text-fg-secondary">
              {'Your current IP: '}
              <span data-testid="client-ip" className="font-mono text-mono text-fg">
                {clientIp}
              </span>
              {' — '}
              <button
                type="button"
                className="rounded-sm font-medium text-indigo hover:underline"
                onClick={() => {
                  // Prefill only — the admin still names it and presses "Add
                  // network". A one-click write would arm a premises rule on a
                  // misread sentence.
                  setCidr(singleHostCidr(clientIp));
                  setCidrError(null);
                  cidrFieldRef.current?.focus();
                }}
              >
                add this network
              </button>
            </p>
          )}

          <form
            className="flex flex-wrap items-start gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="w-full max-w-sm">
              <TextField
                label="Label"
                value={label}
                maxLength={NETWORK_RULE_LABEL_MAX}
                placeholder="e.g. Kinshasa office"
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>
            <div className="w-full max-w-sm">
              <TextField
                ref={cidrFieldRef}
                label="Network (CIDR)"
                value={cidr}
                maxLength={NETWORK_RULE_CIDR_MAX}
                placeholder="e.g. 196.223.14.0/24"
                error={cidrError}
                className="font-mono"
                onChange={(event) => {
                  setCidr(event.target.value);
                  if (cidrError !== null) setCidrError(null);
                }}
              />
            </div>
            {/* mt-[26px] aligns the button with the field boxes (label 16px + 6px gap + 4px). */}
            <Button
              type="submit"
              className="mt-[26px]"
              disabled={label.trim() === '' || cidr.trim() === '' || create.isPending}
            >
              {create.isPending ? 'Adding…' : 'Add network'}
            </Button>
          </form>
        </Card>

        <Card title={`Networks in ${countryName}`}>
          {rules.isPending ? (
            <SkeletonRows rows={3} label="Loading network rules" />
          ) : rules.isError ? (
            <EmptyState
              title="Network rules could not be loaded"
              description={errorSentence(rules.error, 'The server did not answer.')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck aria-hidden="true" className="size-5" />}
              title="No networks registered"
              description="Add the office network above to start restricting where this country works from."
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Label
                  </th>
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Network
                  </th>
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Status
                  </th>
                  <th scope="col" className="py-2 pr-3 font-normal">
                    Added
                  </th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <NetworkRuleRow
                    key={row.id}
                    row={row}
                    onDeactivate={() => {
                      setActionError(null);
                      setDeactivating(row);
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={deactivating !== null}
        title={`Deactivate '${deactivating?.label ?? ''}'?`}
        body={`Devices on ${deactivating?.cidr ?? ''} stop counting as on-premises immediately. The rule stays in this list as the audit record; deactivating the last active rule makes ${countryName} unrestricted again.`}
        confirmLabel="Deactivate rule"
        busy={deactivate.isPending}
        onConfirm={() => {
          if (deactivating !== null) deactivate.mutate(deactivating.id);
        }}
        onCancel={() => setDeactivating(null)}
      />
    </div>
  );
}

function NetworkRuleRow({
  row,
  onDeactivate,
}: {
  row: NetworkRuleDto;
  onDeactivate: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      {/* Secondary, not muted, for the deactivated name — same AA reasoning as
        * the revoked agent-key label (2.63:1 would fail on light). */}
      <td className={`py-2.5 pr-3 text-body ${row.active ? 'text-fg' : 'text-fg-secondary'}`}>
        {row.label}
      </td>
      <td className="py-2.5 pr-3 font-mono text-mono text-fg-secondary">{row.cidr}</td>
      <td className="py-2.5 pr-3">
        {row.active ? (
          <span className="inline-flex items-center rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg">
            Active
          </span>
        ) : (
          /* Bordered and unfilled — the filled version's 4.4:1 (secondary on
           * subtle, light) is under AA; see the agent-keys pill comment. */
          <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
            Deactivated
          </span>
        )}
      </td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        <time dateTime={row.created_at}>{relativeTime(row.created_at)}</time>
      </td>
      <td className="py-2.5 text-right">
        {row.active ? (
          /* Mode-forked danger text — same AA reasoning and classes as the
           * agent-keys Revoke… button (5.52 light / 13.49 dark; hover 4.52). */
          <Button
            variant="quiet"
            className="text-danger-fg hover:bg-danger-bg hover:text-danger-fg dark:text-danger-bg dark:hover:text-danger-fg"
            onClick={onDeactivate}
          >
            Deactivate…
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
